import { convertDimensionUnits, memoEval } from '@salsita-ai/platform-project-sdk/domain';
import { createRecipe, implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js';
import { Base, BaseFourLeg, BaseTypes, BoatShapeTop, EdgeStyles, EllipticalOvalTop, LeafTypes, RacetrackOvalTop, RectangleTop, RoundTop, SeatingTypes, SquareTop } from '../__generated/model/index.js';
import { PRICING_CONFIG } from '../data/pricing-config.js';
import { SEATING_MAP } from '../data/table-data.js';
import { FINISH_MAP, SHEEN_MAP, WOOD_SPECIES_MAP, WOOD_SPECIES_TEXTURE_URLS } from '../data/texture-data.js';
import { THICKNESS_OPTIONS, TOP_DISTRESS_LEVEL_OPTIONS, TOP_EDGE_STYLE_OPTIONS } from '../data/top-data.js';
export const tableExpressions = implementCustomExpressions({
    Table: {
        $pricingConfig: (_s, _state, ctx)=>{
            const published = ctx.publishedData.pricing?.PricingConfig?.[0]?.data;
            if (published) {
                try {
                    return {
                        ...PRICING_CONFIG,
                        ...JSON.parse(published)
                    };
                } catch  {
                    return PRICING_CONFIG;
                }
            }
            return PRICING_CONFIG;
        },
        $customProductTitle: (s)=>{
            return `${s.$top.$topDataMap[s.$top.type].label} - ${s.$fullWidth.toFixed(2)} x ${s.$fullLength.toFixed(2)}`;
        },
        $customProductDescription: (s)=>{
            /**
       * Generates a comprehensive HTML product description for Shopify based on table configuration
       */ const sections = [];
            // Table Top Section
            const topSection = s.$top.$customProductTopDescription // generateTopDescriptionHTML(s)
            ;
            if (topSection) sections.push(topSection);
            // Base Section
            const baseSection = s.$base.$customProductBaseDescription;
            if (baseSection) sections.push(baseSection);
            // Materials & Finishes Section
            const materialsSection = s.$materialAndFinishesDescriptionHTML;
            if (materialsSection) sections.push(materialsSection);
            // Accessories Section
            const accessoriesSection = s.$accessoriesDescriptionHTML;
            if (accessoriesSection) sections.push(accessoriesSection);
            return sections.join('\n\n');
        },
        $accessoriesDescriptionHTML: (s)=>{
            const accessories = [];
            // Add seating information
            if (s.seatingType !== SeatingTypes.NoSeating) {
                const seatingData = SEATING_MAP[s.seatingType];
                const seatingText = s.seatingType === SeatingTypes.Benches ? `${s.seatingAmount}x ${seatingData.label}` : seatingData.label;
                accessories.push(seatingText);
            }
            if (accessories.length === 0) {
                return '';
            }
            return `<h3>Accessories</h3>\n<ul><li><strong>Seating:</strong> ${accessories.join(', ')}</li></ul>`;
        },
        $materialAndFinishesDescriptionHTML: (s)=>{
            const woodSpecies = WOOD_SPECIES_MAP[s.woodSpecies];
            const sheen = SHEEN_MAP[s.sheen];
            let description = `<h3>Materials & Finishes</h3>\n<ul>`;
            description += `<li><strong>Wood Species:</strong> ${woodSpecies.label}</li>`;
            description += `<li><strong>Sheen:</strong> ${sheen.label}</li>`;
            description += `</ul>`;
            return description;
        },
        $apronIsMandatory: (s)=>BaseFourLeg.is(s.$base),
        $thicknessPrice: (s)=>{
            const p = s.$pricingConfig;
            return p.thickness[s.$top.thicknessType] ?? 0;
        },
        $edgeStylePrice: (s)=>{
            const p = s.$pricingConfig;
            return p.edgeStyles[s.$top.edgeStyle] ?? 0;
        },
        $leafPrice: (s)=>{
            const p = s.$pricingConfig;
            return (p.leaves[s.$top.leafType] ?? 0) * s.$top.leafAmount;
        },
        $apronPrice: (s)=>{
            const p = s.$pricingConfig;
            return p.aprons[s.$top.$apron.type] ?? 0;
        },
        $seatingPrice: (s)=>{
            const p = s.$pricingConfig;
            return (p.seating[s.seatingType] ?? 0) * s.seatingAmount;
        },
        $distressPrice: (s)=>{
            const p = s.$pricingConfig;
            return p.distressLevels[s.$top.distressLevel] ?? 0;
        },
        $topFinishPrice: (s)=>{
            const p = s.$pricingConfig;
            return p.finishes[s.$top.finish] ?? 0;
        },
        $baseFinishPrice: (s)=>{
            const p = s.$pricingConfig;
            return p.finishes[s.$base.finish] ?? 0;
        },
        $sheenPrice: (s)=>{
            const p = s.$pricingConfig;
            return p.sheens[s.sheen] ?? 0;
        },
        $groovesPrice: (s)=>{
            const p = s.$pricingConfig;
            return s.$top.withGrooves ? p.grooves : 0;
        },
        $cornerRadiusPrice: (s)=>{
            const p = s.$pricingConfig;
            return s.$top.withCornerRadius ? p.cornerRadius : 0;
        },
        $crossbracePrice: (s)=>{
            const p = s.$pricingConfig;
            return s.$base.hasCrossbrace ? p.crossbrace : 0;
        },
        $woodSpeciesRate: (s)=>{
            const p = s.$pricingConfig;
            return p.woodSpecies[s.woodSpecies] ?? 0;
        },
        $woodPartsTotal: (s)=>{
            const isMetalBase = s.$base.$baseDataMap[s.$base.type].baseType === BaseTypes.Metal;
            return s.$top.$price + s.$leafPrice + (isMetalBase ? 0 : s.$base.$price) + s.$apronPrice + s.$seatingPrice;
        },
        $woodSpeciesPrice: (s)=>Math.round(s.$woodPartsTotal * s.$woodSpeciesRate),
        $topSubtotal: (s)=>s.$top.$price + s.$thicknessPrice + s.$edgeStylePrice + s.$leafPrice + s.$apronPrice + s.$distressPrice + s.$topFinishPrice + s.$groovesPrice + s.$cornerRadiusPrice,
        $baseSubtotal: (s)=>s.$base.$price + s.$baseFinishPrice + s.$crossbracePrice,
        $materialsSubtotal: (s)=>s.$woodSpeciesPrice + s.$sheenPrice,
        $accessoriesSubtotal: (s)=>s.$seatingPrice,
        $price: (s)=>{
            return s.$topSubtotal + s.$baseSubtotal + s.$materialsSubtotal + s.$accessoriesSubtotal;
        },
        $topOptions: (s)=>{
            const commonProperties = {
                distressLevel: TOP_DISTRESS_LEVEL_OPTIONS.Clean.type,
                edgeStyle: EdgeStyles.Clean,
                withGrooves: false,
                withCornerRadius: false,
                leafType: LeafTypes.NoLeaf,
                leafAmount: s.$top.$leafDataMap[LeafTypes.NoLeaf].purchasableAmounts[0],
                finish: s.$top.finish
            };
            return [
                {
                    // Review: Having these taken from a record doesn't bring much value, inline would be easier to understand
                    label: s.$top.$topDataMap[RectangleTop.type].label,
                    img: s.$top.$topDataMap[RectangleTop.type].thumbnailUrl,
                    recipes: [
                        createRecipe({
                            type: 'RectangleTop',
                            ...commonProperties,
                            width: s.$top.$topDataMap[RectangleTop.type].sizing.width.default,
                            length: s.$top.$topDataMap[RectangleTop.type].sizing.length.default,
                            thicknessType: s.$top.$topDataMap[RectangleTop.type].sizing.thickness.thicknessTypes[0],
                            thickness: THICKNESS_OPTIONS[s.$top.$topDataMap[RectangleTop.type].sizing.thickness.thicknessTypes[0]].defaultValue,
                            apronId: s.$top.$apron.id
                        }),
                        createRecipe({
                            type: s.$top.$apron.type,
                            id: s.$top.$apron.id
                        })
                    ],
                    keepChildren: false
                },
                {
                    label: s.$top.$topDataMap[SquareTop.type].label,
                    img: s.$top.$topDataMap[SquareTop.type].thumbnailUrl,
                    recipes: [
                        createRecipe({
                            type: 'SquareTop',
                            ...commonProperties,
                            side: s.$top.$topDataMap[SquareTop.type].sizing.side.default,
                            thicknessType: s.$top.$topDataMap[SquareTop.type].sizing.thickness.thicknessTypes[0],
                            thickness: THICKNESS_OPTIONS[s.$top.$topDataMap[SquareTop.type].sizing.thickness.thicknessTypes[0]].defaultValue,
                            apronId: s.$top.$apron.id
                        }),
                        createRecipe({
                            type: s.$top.$apron.type,
                            id: s.$top.$apron.id
                        })
                    ],
                    keepChildren: false
                },
                {
                    label: s.$top.$topDataMap[RoundTop.type].label,
                    img: s.$top.$topDataMap[RoundTop.type].thumbnailUrl,
                    recipes: [
                        createRecipe({
                            type: 'RoundTop',
                            ...commonProperties,
                            diameter: s.$top.$topDataMap[RoundTop.type].sizing.diameter.default,
                            thicknessType: s.$top.$topDataMap[RoundTop.type].sizing.thickness.thicknessTypes[0],
                            thickness: THICKNESS_OPTIONS[s.$top.$topDataMap[RoundTop.type].sizing.thickness.thicknessTypes[0]].defaultValue,
                            apronId: s.$top.$apron.id
                        }),
                        createRecipe({
                            type: s.$top.$apron.type,
                            id: s.$top.$apron.id
                        })
                    ],
                    keepChildren: false
                },
                {
                    label: s.$top.$topDataMap[EllipticalOvalTop.type].label,
                    img: s.$top.$topDataMap[EllipticalOvalTop.type].thumbnailUrl,
                    recipes: [
                        createRecipe({
                            type: 'EllipticalOvalTop',
                            ...commonProperties,
                            width: s.$top.$topDataMap[EllipticalOvalTop.type].sizing.width.default,
                            length: s.$top.$topDataMap[EllipticalOvalTop.type].sizing.length.default,
                            thicknessType: s.$top.$topDataMap[EllipticalOvalTop.type].sizing.thickness.thicknessTypes[0],
                            thickness: THICKNESS_OPTIONS[s.$top.$topDataMap[EllipticalOvalTop.type].sizing.thickness.thicknessTypes[0]].defaultValue,
                            apronId: s.$top.$apron.id
                        }),
                        createRecipe({
                            type: s.$top.$apron.type,
                            id: s.$top.$apron.id
                        })
                    ],
                    keepChildren: false
                },
                {
                    label: s.$top.$topDataMap[RacetrackOvalTop.type].label,
                    img: s.$top.$topDataMap[RacetrackOvalTop.type].thumbnailUrl,
                    recipes: [
                        createRecipe({
                            type: 'RacetrackOvalTop',
                            ...commonProperties,
                            width: s.$top.$topDataMap[RacetrackOvalTop.type].sizing.width.default,
                            length: s.$top.$topDataMap[RacetrackOvalTop.type].sizing.length.default,
                            thicknessType: s.$top.$topDataMap[RacetrackOvalTop.type].sizing.thickness.thicknessTypes[0],
                            thickness: THICKNESS_OPTIONS[s.$top.$topDataMap[RacetrackOvalTop.type].sizing.thickness.thicknessTypes[0]].defaultValue,
                            apronId: s.$top.$apron.id
                        }),
                        createRecipe({
                            type: s.$top.$apron.type,
                            id: s.$top.$apron.id
                        })
                    ],
                    keepChildren: false
                },
                {
                    label: s.$top.$topDataMap[BoatShapeTop.type].label,
                    img: s.$top.$topDataMap[BoatShapeTop.type].thumbnailUrl,
                    recipes: [
                        createRecipe({
                            type: 'BoatShapeTop',
                            ...commonProperties,
                            width: s.$top.$topDataMap[BoatShapeTop.type].sizing.width.default,
                            length: s.$top.$topDataMap[BoatShapeTop.type].sizing.length.default,
                            thicknessType: s.$top.$topDataMap[BoatShapeTop.type].sizing.thickness.thicknessTypes[0],
                            thickness: THICKNESS_OPTIONS[s.$top.$topDataMap[BoatShapeTop.type].sizing.thickness.thicknessTypes[0]].defaultValue,
                            apronId: s.$top.$apron.id
                        }),
                        createRecipe({
                            type: s.$top.$apron.type,
                            id: s.$top.$apron.id
                        })
                    ],
                    keepChildren: false
                }
            ];
        },
        $baseStyleOptions: (s)=>{
            // Filter baseStyle options per the selected baseType AND top compatibility
            const basesMatchingType = Base.types.filter((baseType)=>s.$base.$baseDataMap[baseType].baseType === s.baseType);
            const availableBaseStyles = basesMatchingType.filter((baseStyle)=>s.$top.$availableBaseStyles.includes(baseStyle));
            const baseStyleOptions = availableBaseStyles.map((baseStyle)=>{
                const targetSupportsCrossBrace = s.$base.$baseDataMap[baseStyle].supportsCrossBrace;
                return {
                    label: s.$base.$baseDataMap[baseStyle].label,
                    img: s.$base.$baseDataMap[baseStyle].thumbnailUrl,
                    recipes: [
                        createRecipe({
                            type: baseStyle,
                            hasCrossbrace: targetSupportsCrossBrace ? s.$base.hasCrossbrace : false,
                            finish: s.$base.finish
                        })
                    ]
                };
            }).sort((a, b)=>a.label.localeCompare(b.label));
            return baseStyleOptions;
        },
        $seatingAmountOptions: memoEval((s)=>[
                s.seatingType
            ], (seatingType)=>SEATING_MAP[seatingType].purchasableAmounts.map((amount)=>({
                    label: amount.toString(),
                    value: amount
                }))),
        $fullWidth: (s)=>{
            if (s.$top.$leafPerimeterVisible) {
                return s.$top.$leafPerimeterOuterDiameter;
            }
            const topWidth = s.$top.$width;
            let maxLeafWidth = 0;
            if (s.$top.leafType !== LeafTypes.NoLeaf) {
                maxLeafWidth = s.$top.$leafWidth;
            }
            const largerWidth = topWidth > maxLeafWidth ? topWidth : maxLeafWidth;
            return largerWidth;
        },
        $fullLength: (s)=>{
            if (s.$top.$leafPerimeterVisible) {
                return s.$top.$leafPerimeterOuterDiameter;
            }
            const topLength = s.$top.$length;
            let fullLeafLength = 0;
            if (s.$top.$leafLeftVisible) {
                fullLeafLength += s.$top.$leafLength;
            }
            if (s.$top.$leafRightVisible) {
                fullLeafLength += s.$top.$leafLength;
            }
            if (s.$top.$leafCenterVisible) {
                fullLeafLength += s.$top.$leafLength * s.$top.leafAmount;
            }
            return topLength + fullLeafLength;
        },
        // ----------------------------------Wood Repeat Ratio (derived from texture size in cm)----------------------------------
        // Converts textureSizeCm to inches for use in repeat calculations (dimensions are in inches)
        $woodTextureSize: (s)=>convertDimensionUnits(WOOD_SPECIES_TEXTURE_URLS[s.woodSpecies].textureSizeCm, 'cm', 'inch'),
        $tableData: (s)=>{
            const result = [
                {
                    item: s.$top.$topDataMap[s.$top.type].label,
                    description: s.$top.$sizeDescription,
                    price: s.$top.$price
                },
                {
                    item: s.$base.$baseDataMap[s.$base.type].label,
                    description: 'Base',
                    price: s.$base.$price
                }
            ];
            if (s.$thicknessPrice !== 0) {
                result.push({
                    item: `${THICKNESS_OPTIONS[s.$top.thicknessType].label} Thickness`,
                    price: s.$thicknessPrice
                });
            }
            if (s.$edgeStylePrice !== 0) {
                result.push({
                    item: `${TOP_EDGE_STYLE_OPTIONS[s.$top.edgeStyle].label} Edge`,
                    price: s.$edgeStylePrice
                });
            }
            if (s.$top.leafType !== LeafTypes.NoLeaf && s.$leafPrice !== 0) {
                result.push({
                    item: `${s.$top.$leafDataMap[s.$top.leafType].label} × ${s.$top.leafAmount}`,
                    price: s.$leafPrice
                });
            }
            if (s.$woodSpeciesPrice !== 0) {
                result.push({
                    item: WOOD_SPECIES_MAP[s.woodSpecies].label,
                    description: 'Wood species upgrade',
                    price: s.$woodSpeciesPrice
                });
            }
            if (s.$top.$apron.type !== 'ApronNone') {
                result.push({
                    item: 'Apron',
                    price: s.$apronPrice
                });
            }
            if (s.$seatingPrice !== 0) {
                result.push({
                    item: `Bench × ${s.seatingAmount}`,
                    price: s.$seatingPrice
                });
            }
            if (s.$distressPrice !== 0) {
                result.push({
                    item: `${TOP_DISTRESS_LEVEL_OPTIONS[s.$top.distressLevel].label} Distress`,
                    price: s.$distressPrice
                });
            }
            if (s.$topFinishPrice !== 0) {
                result.push({
                    item: `${FINISH_MAP[s.$top.finish].label} Top Finish`,
                    price: s.$topFinishPrice
                });
            }
            if (s.$baseFinishPrice !== 0) {
                result.push({
                    item: `${FINISH_MAP[s.$base.finish].label} Base Finish`,
                    price: s.$baseFinishPrice
                });
            }
            if (s.$sheenPrice !== 0) {
                result.push({
                    item: `${SHEEN_MAP[s.sheen].label} Sheen`,
                    price: s.$sheenPrice
                });
            }
            if (s.$groovesPrice !== 0) {
                result.push({
                    item: 'Grooves',
                    price: s.$groovesPrice
                });
            }
            if (s.$cornerRadiusPrice !== 0) {
                result.push({
                    item: 'Corner Radius',
                    price: s.$cornerRadiusPrice
                });
            }
            if (s.$crossbracePrice !== 0) {
                result.push({
                    item: 'Cross Brace',
                    price: s.$crossbracePrice
                });
            }
            result.push({
                item: 'Total',
                description: 'Total',
                price: s.$price
            });
            return result;
        }
    }
});
export const tableInvariants = implementCustomInvariants({
    Table: {
        seatingAmountNotAvailableForThisSeatingType: (s)=>{
            return SEATING_MAP[s.seatingType].purchasableAmounts.includes(s.seatingAmount);
        }
    }
});

//# sourceMappingURL=table.js.map